// ═══════════════════════════════════════════════════════════════
// Shell.jsx — verticalstack unified application shell
// Path: src/components/Shell.jsx
//
// S31 LAYER 1 (Apr 27, 2026) — NEW FILE for sandbox-schema migration.
// Per Apr 27 collapse-now decision (Migration Change Matrix v1.1).
//
// S7.A EDIT (May 21, 2026) — forward `setCurrentSection` as
//   `onSectionChange` prop to PlaceholderDashboard. Enables Team.jsx
//   tenant/vendor empty-state Messages cross-link to switch sections
//   (Guardrail #11). Backward-compatible: existing dispatch targets
//   that don't use the prop ignore it.
//
// PURPOSE
//   Replaces the 4-portal model (AdminApp / OwnerApp / PMApp / TenantApp).
//   One unified shell that adapts to the user's role(s) in the currently
//   selected workspace.
//
// LAYOUT
//   ┌─────────────────────────────────────────────────────────────┐
//   │ TOP BAR: workspace switcher · user menu                     │
//   ├──────────┬──────────────────────────────────────────────────┤
//   │          │                                                  │
//   │ ShellNav │  CONTENT AREA                                    │
//   │  (left)  │  (PlaceholderDashboard in Layer 1)               │
//   │          │                                                  │
//   └──────────┴──────────────────────────────────────────────────┘
//
// CONTENT ROUTING (Layer 1)
//   currentSection state tracks which nav item is selected.
//   Default: 'overview'. All sections render PlaceholderDashboard
//   in Layer 1 — content scoping happens via the section prop.
//   Layer 2+ replaces PlaceholderDashboard per-section with real
//   role-scoped dashboard components.
//
// STYLING
//   Neutral professional formatting per memory #30 (branding deferred).
//   Inline styles only.
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useWorkspace } from '../hooks/useWorkspace';
import { useMembership } from '../hooks/useMembership';
import { getRolesLabel } from '../config/roles';
import ShellNav from './ShellNav';
import PlaceholderDashboard from './PlaceholderDashboard';

// ── Inline styles ────────────────────────────────────────────────────────

const styles = {
  shell: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#F8F9FA',
    overflow: 'hidden',
  },
  topBar: {
    height: 56,
    minHeight: 56,
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #E5E7EB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    flexShrink: 0,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  brandText: {
    fontSize: 16,
    fontWeight: 700,
    color: '#003060',
    letterSpacing: 0.2,
  },
  brandTagline: {
    fontSize: 11,
    color: '#9CA3AF',
    marginLeft: 4,
  },
  topBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  workspaceSwitcher: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    border: '1px solid #E5E7EB',
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
    fontSize: 14,
    color: '#003060',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  workspaceSwitcherSelect: {
    border: 'none',
    background: 'transparent',
    fontSize: 14,
    fontWeight: 600,
    color: '#003060',
    cursor: 'pointer',
    fontFamily: 'inherit',
    outline: 'none',
    paddingRight: 4,
  },
  userMenu: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 13,
    color: '#6B7280',
  },
  userEmail: {
    color: '#003060',
    fontWeight: 600,
  },
  signOutButton: {
    border: '1px solid #E5E7EB',
    background: 'transparent',
    color: '#6B7280',
    fontSize: 12,
    fontWeight: 600,
    padding: '6px 12px',
    borderRadius: 4,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  body: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: 32,
  },
  emptyContent: {
    padding: 48,
    textAlign: 'center',
    color: '#6B7280',
    fontSize: 15,
  },
  loadingContent: {
    padding: 48,
    textAlign: 'center',
    color: '#9CA3AF',
    fontSize: 14,
  },
};

// ── Workspace switcher subcomponent ──────────────────────────────────────

function WorkspaceSwitcher({ availableWorkspaces, currentWorkspaceId, onSwitch }) {
  if (!availableWorkspaces || availableWorkspaces.length <= 1) {
    // Single workspace — render as static label, no dropdown
    const ws = availableWorkspaces?.[0];
    return (
      <div style={styles.workspaceSwitcher}>
        <span>{ws ? ws.workspaceName : 'No workspace'}</span>
      </div>
    );
  }

  return (
    <div style={styles.workspaceSwitcher}>
      <select
        value={currentWorkspaceId || ''}
        onChange={(e) => onSwitch(e.target.value)}
        style={styles.workspaceSwitcherSelect}
        aria-label="Switch workspace"
      >
        {availableWorkspaces.map((ws) => (
          <option key={ws.workspaceId} value={ws.workspaceId}>
            {ws.workspaceName}
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────

export default function Shell() {
  const { profile, logout } = useAuth();
  const {
    availableWorkspaces,
    currentWorkspaceId,
    currentWorkspace,
    switchWorkspace,
    loading: workspaceLoading,
  } = useWorkspace();
  const { roles, loading: membershipLoading } = useMembership();

  const [currentSection, setCurrentSection] = useState('overview');

  // Loading state — wait for workspace + membership before rendering body
  const isLoading = workspaceLoading || membershipLoading;

  // No workspace selected (shouldn't happen if App.js routes correctly,
  // but defensive — would mean user landed on Shell without a workspace)
  if (!currentWorkspaceId && !isLoading) {
    return (
      <div style={styles.shell}>
        <div style={styles.topBar}>
          <div style={styles.brand}>
            <span style={styles.brandText}>verticalstack</span>
            <span style={styles.brandTagline}>· stack more, manage less</span>
          </div>
        </div>
        <div style={styles.emptyContent}>
          No workspace selected. Return to the workspace picker to continue.
        </div>
      </div>
    );
  }

  const userEmail = profile?.email || '—';

  return (
    <div style={styles.shell}>
      {/* ── Top bar ─────────────────────────────────────────────────── */}
      <div style={styles.topBar}>
        <div style={styles.brand}>
          <span style={styles.brandText}>verticalstack</span>
          <span style={styles.brandTagline}>· stack more, manage less</span>
        </div>

        <div style={styles.topBarRight}>
          <WorkspaceSwitcher
            availableWorkspaces={availableWorkspaces}
            currentWorkspaceId={currentWorkspaceId}
            onSwitch={switchWorkspace}
          />

          <div style={styles.userMenu}>
            <span style={styles.userEmail}>{userEmail}</span>
            <button
              type="button"
              style={styles.signOutButton}
              onClick={() => logout()}
              aria-label="Sign out"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* ── Body: nav + content ─────────────────────────────────────── */}
      <div style={styles.body}>
        <ShellNav
          currentSection={currentSection}
          onSectionChange={setCurrentSection}
        />

        <main style={styles.content}>
          {isLoading ? (
            <div style={styles.loadingContent}>Loading workspace…</div>
          ) : (
            <PlaceholderDashboard
              section={currentSection}
              workspace={currentWorkspace}
              roles={roles}
              roleLabel={getRolesLabel(roles)}
              onSectionChange={setCurrentSection}
            />
          )}
        </main>
      </div>
    </div>
  );
}
